import React from 'react';
import { FormattedMessage } from 'react-intl';
import { css } from 'aphrodite/no-important';

import type { Product } from '@contactcentre-web/redux-common/types/Product';
import { VendorRegion } from '@contactcentre-web/hooks/api/useOrderHistory';
import FormattedCurrency from '@contactcentre-web/common/FormattedCurrency';
import OriginToDestination from '@contactcentre-web/common/OriginToDestination/OriginToDestination';
import {
  isLocalAreaProduct,
  isRailcardProduct,
  isSeasonProduct,
  isTravelProduct,
} from '@contactcentre-web/refunds/utils';

import { RequestCompensationPayload } from '../../sagas';

import styles from './stylesProductsForm';
import messages from './messages';

export const mapProductToCheckbox = (product: Product) => {
  if (isTravelProduct(product)) {
    return {
      value: product.id,
      label: (
        <>
          <OriginToDestination
            isReturn={product.isReturn}
            origin={product.origin}
            destination={product.destination}
            containerStyle={styles.productLabel}
          />
          {product.vendorRegion === VendorRegion.EU && (
            <span className={css(styles.pnr)}>
              <small>PNR</small>
              <strong>{product.transactionReference}</strong>
            </span>
          )}
          {product.transactionTotalPrice && (
            <div>
              <FormattedMessage {...messages.productPriceLabel} />:{' '}
              <FormattedCurrency
                amount={product.transactionTotalPrice.amount}
                currencyCode={product.transactionTotalPrice.currencyCode}
              />
            </div>
          )}
        </>
      ),
    };
  }

  if (isRailcardProduct(product)) {
    return {
      value: product.id,
      label: (
        <FormattedMessage
          {...messages.railcardLabel}
          values={{
            name: product.name,
            railcardNumber: product.railcardNumber,
          }}
        />
      ),
    };
  }

  if (isSeasonProduct(product)) {
    return {
      value: product.id,
      label: (
        <>
          <OriginToDestination
            isReturn
            origin={product.journey.origin}
            destination={product.journey.destination}
            containerStyle={styles.productLabel}
          />
          {product.vendorRegion === VendorRegion.EU && (
            <span className={css(styles.pnr)}>
              <small>PNR</small>
              <strong>{product.transactionReference}</strong>
            </span>
          )}
        </>
      ),
    };
  }

  if (isLocalAreaProduct(product)) {
    return {
      value: product.id,
      label: (
        <>
          <div className={css(styles.productLabel)}>{product.localAreaValidity}</div>
          {product.transactionTotalPrice && (
            <div>
              <FormattedMessage {...messages.productPriceLabel} />:{' '}
              <FormattedCurrency
                amount={product.transactionTotalPrice.amount}
                currencyCode={product.transactionTotalPrice.currencyCode}
              />
            </div>
          )}
        </>
      ),
    };
  }

  return {
    value: product.id,
    label: (
      <FormattedMessage
        {...messages.unknownLabel}
        values={{
          productType: product.type,
          id: product.id,
        }}
      />
    ),
  };
};

export const mapRequestProductValuesPayload = (
  productValues: Record<
    string,
    {
      checked: boolean;
      value: string;
    }
  >,
  products: Product[],
  currencyCode: string
) =>
  Object.entries(productValues).reduce((acc, [key, selectedProduct]) => {
    const vendors = mapProductVendor(products);
    if (!selectedProduct.checked) {
      return acc;
    }

    return [
      ...(acc || []),
      {
        value: {
          currencyCode,
          amount: parseFloat(selectedProduct.value),
        },
        product: {
          productId: key,
          vendor: vendors?.[key],
        },
      },
    ];
  }, [] as RequestCompensationPayload['productValues']);

export const mapProductVendor = (products: Product[]) =>
  products.reduce(
    (acc, product) => ({ ...acc, [product.id]: product?.vendor }),
    {} as Record<string, string | undefined>
  );
